import React, { useEffect, useState } from "react";
import { Button, Input, Modal, Typography } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;

interface ColDef {
  column_name: string;
  inferred_type: string;
}

interface Props {
  open: boolean;
  rowIndex: number | null;
  values: Record<string, any>;
  columns: ColDef[];
  onSave: (
    rowIndex: number,
    values: Record<string, any>,
    changedColumns: string[],
  ) => void;
  onClose: () => void;
}

const EditDataModal: React.FC<Props> = ({
  open,
  rowIndex,
  values,
  columns,
  onSave,
  onClose,
}) => {
  const { t } = useTranslation("dataCleaning");
  const [formValues, setFormValues] = useState<Record<string, any>>({});

  useEffect(() => {
    if (open) {
      setFormValues({ ...values });
    }
  }, [open, values]);

  const handleSave = () => {
    if (rowIndex == null) return;

    const changedColumns = columns
      .map((c) => c.column_name)
      .filter((col) => formValues[col] !== values[col]);

    onSave(rowIndex, formValues, changedColumns);
  };

  const isNumeric = (inferred: string) => {
    const t = (inferred ?? "").toLowerCase();
    return t.includes("int") || t.includes("float") || t === "numeric";
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      centered
      width={400}
      className="edit-data-modal"
    >
      <div className="edit-data-modal__header">
        <div />
        <CloseOutlined
          style={{ color: "#8c6cff", cursor: "pointer", fontSize: 16 }}
          onClick={onClose}
        />
      </div>

      <Title
        level={5}
        style={{
          textAlign: "center",
          color: "#4a1c96",
          fontFamily: "Poppins",
          marginBottom: 20,
        }}
      >
        {t("editModal.title")}
      </Title>

      <div className="edit-data-modal__fields">
        {columns.map((col) => (
          <div key={col.column_name} className="edit-data-modal__field">
            <Text className="edit-data-modal__label">{col.column_name}</Text>
            <Input
              value={formValues[col.column_name] ?? ""}
              type={isNumeric(col.inferred_type) ? "number" : "text"}
              onChange={(e) =>
                setFormValues((prev) => ({
                  ...prev,
                  [col.column_name]: e.target.value,
                }))
              }
              style={{
                borderRadius: 6,
                fontFamily: "Poppins",
                fontSize: 13,
              }}
            />
          </div>
        ))}
      </div>

      <div className="edit-data-modal__actions">
        <Button
          style={{
            minWidth: 90,
            borderColor: "#4a1c96",
            color: "#4a1c96",
            fontFamily: "Poppins",
            borderRadius: 8,
          }}
          onClick={onClose}
        >
          {t("actions.cancel")}
        </Button>
        <Button
          type="primary"
          style={{
            minWidth: 120,
            background: "#4a1c96",
            borderColor: "#4a1c96",
            fontFamily: "Poppins",
            borderRadius: 8,
          }}
          onClick={handleSave}
        >
          {t("actions.saveChanges")}
        </Button>
      </div>
    </Modal>
  );
};

export default EditDataModal;
